import React, { useRef, useState, useCallback } from "react";
import { IconButton, Tooltip, TablePagination } from "@mui/material";
import { Edit, DeleteOutline, ChevronLeft, ChevronRight } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Image from "src/shared/Image";
import { Category, Product } from "src/shared/services/interfacev2";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";

interface ProductGridViewProps {
  products: Product[];
  categories: Category[];
  showDeletedProducts: boolean;
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onReorderProduct?: (productId: string, sourceIndex: number, targetIndex: number) => void;
}

const ProductGridView: React.FC<ProductGridViewProps> = ({
  products,
  categories,
  showDeletedProducts,
  page,
  rowsPerPage,
  totalRecords,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
  onReorderProduct,
}) => {
  const getCategoryName = (categoryId: string): string => categories.find((cat) => cat._id.$oid === categoryId)?.name || "Unknown";

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [touchHighlight, setTouchHighlight] = useState<number | null>(null);

  const touchDragIndex = useRef<number | null>(null);

  // Scroll container reference
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto scroll function
  const autoScroll = (clientY: number) => {
    const container = scrollRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const threshold = 80;
    const speed = 16;

    if (clientY > rect.bottom - threshold) {
      container.scrollTop += speed;
    } else if (clientY < rect.top + threshold) {
      container.scrollTop -= speed;
    }
  };

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      autoScroll(e.clientY);

      if (index !== draggedIndex) {
        setDragOverIndex(index);
      }
    },
    [draggedIndex],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
      e.preventDefault();

      if (draggedIndex === null || draggedIndex === targetIndex || !onReorderProduct) return;

      onReorderProduct(products[draggedIndex]._id.$oid, draggedIndex, targetIndex);

      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, products, onReorderProduct],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Touch drag
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>, index: number) => {
    touchDragIndex.current = index;
    setTouchHighlight(index);
    setDraggedIndex(index);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (touchDragIndex.current === null) return;

    const touch = e.touches[0];

    autoScroll(touch.clientY);

    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = el?.closest("[data-grid-index]") as HTMLElement | null;

    if (card) {
      const idx = Number(card.dataset.gridIndex);

      if (!isNaN(idx) && idx !== touchDragIndex.current) {
        setDragOverIndex(idx);
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const src = touchDragIndex.current;

    if (src !== null && dragOverIndex !== null && src !== dragOverIndex && onReorderProduct) {
      onReorderProduct(products[src]._id.$oid, src, dragOverIndex);
    }

    touchDragIndex.current = null;
    setTouchHighlight(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [dragOverIndex, products, onReorderProduct]);

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="flex flex-col w-full gap-3">
      {/* Scrollable Grid */}
      <div ref={scrollRef} className="w-full h-[68vh] overflow-y-auto thin-scrollbar">
        {products.length === 0 ? (
          // No data message
          <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
            <div className="text-center">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
              <p className="text-gray-500">{showDeletedProducts ? "No deleted products match your criteria" : "No products match your search criteria"}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 p-2 select-none">
            {products.map((item, index) => {
              const isDragging = draggedIndex === index;
              const isOver = dragOverIndex === index && !isDragging;
              const isTouched = touchHighlight === index;
              const isReorderEnabled = !!onReorderProduct;

              return (
                <div
                  key={item._id.$oid}
                  data-grid-index={index}
                  draggable={isReorderEnabled}
                  onDragStart={isReorderEnabled ? (e) => handleDragStart(e, index) : undefined}
                  onDragOver={isReorderEnabled ? (e) => handleDragOver(e, index) : undefined}
                  onDrop={isReorderEnabled ? (e) => handleDrop(e, index) : undefined}
                  onDragEnd={isReorderEnabled ? handleDragEnd : undefined}
                  onTouchStart={isReorderEnabled ? (e) => handleTouchStart(e, index) : undefined}
                  onTouchMove={isReorderEnabled ? handleTouchMove : undefined}
                  onTouchEnd={isReorderEnabled ? handleTouchEnd : undefined}
                  style={{
                    opacity: isDragging ? 0.45 : 1,
                    transform: isOver ? "scale(1.04)" : "scale(1)",
                    boxShadow: isTouched ? "0 0 0 3px #3b82f6, 0 8px 24px rgba(59,130,246,.35)" : isOver ? "0 0 0 2px #6366f1, 0 4px 16px rgba(99,102,241,.25)" : undefined,
                    cursor: isReorderEnabled ? (isDragging ? "grabbing" : "grab") : "default",
                    transition: "transform .15s ease, box-shadow .15s ease, opacity .15s ease",
                  }}
                  className={`flex flex-col items-center bg-white rounded-xl border p-3 transition-all ${
                    isOver ? "border-indigo-400 bg-indigo-50" : isTouched ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:shadow-md"
                  }`}
                >
                  <div className={`w-full flex ${isReorderEnabled ? "justify-between" : "justify-end"} items-center mb-1`}>
                    {isReorderEnabled && <DragIndicatorIcon fontSize="small" className="text-gray-300 hover:text-blue-500" />}
                    <span className="text-[10px] text-gray-400 font-medium">#{page * rowsPerPage + index + 1}</span>
                  </div>

                  <div className="w-20 h-20 flex items-center justify-center mb-2 rounded-lg bg-gray-50 overflow-hidden">
                    <Image imagePath={item.imageUrl} className="w-full h-full object-cover" />
                  </div>

                  <p className="text-sm font-semibold text-gray-800 text-center line-clamp-2">{item.productName}</p>

                  {item.arabicName && <p className="text-xs text-gray-400 text-center">{item.arabicName}</p>}

                  <p className="text-xs text-gray-500 mt-1">{getCategoryName(item.categoryId)}</p>

                  <p className="text-sm font-bold text-blue-600 mt-1">{Number(item.amount).toFixed(2)} QAR</p>

                  <div className="flex gap-1 mt-2">
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" disabled={showDeletedProducts} onClick={(e) => { e.stopPropagation(); onEdit(item) }} sx={{ color: showDeletedProducts ? "gray" : "black" }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" disabled={showDeletedProducts} onClick={(e) => { e.stopPropagation(); onDelete(item) }} sx={{color: showDeletedProducts ? "gray" : "red" }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {products.length > 0 && (
        <div className="flex items-center justify-end gap-2 px-4 pb-2">
          <Tooltip title="Previous page">
            <span>
              <IconButton size="small" disabled={page === 0} onClick={(e) => onPageChange(e, page - 1)}>
                <ChevronLeft fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              onRowsPerPageChange(newSize);
              onPageChange(null, 0);
            }}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            ActionsComponent={() => null}
          />

          <Tooltip title="Next page">
            <span>
              <IconButton size="small" disabled={page >= totalPages - 1} onClick={(e) => onPageChange(e, page + 1)}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default ProductGridView;
